import type { GetServerSideProps, NextPage } from 'next';
import { NextSeo } from 'next-seo';
import Link from 'next/link';
import { useMemo, useState } from 'react';

import ArticleThumbnail from '@components/common/ArticleThumbnail/ArticleThumbnail';
import Button from '@components/common/Button/Button';
import ErrorBlock from '@components/common/ErrorBlock/ErrorBlock';
import MostPopularCarsSection from '@components/common/MostPopularCarsSection/MostPopularCarsSection';
import SectionWithContainer from '@components/common/SectionWithContainer/SectionWithContainer';
import { Article } from 'models/stack9/Article';

import articleService from 'services/articleService';

export const getServerSideProps: GetServerSideProps = async () => {
  try {
    const articles = await articleService.getAllArticles();

    return { props: { articles } };
  } catch (error) {
    console.error('error', error);
    return { props: { statusCode: 500 } };
  }
};

const OFFSET_ARTICLES = 6;
const BlogsPage: NextPage<{ articles: Article[] }> = ({ articles }) => {
  const [limit, setLimit] = useState(OFFSET_ARTICLES);
  const articlesToRender = useMemo(
    () => articles.slice(0, limit),
    [articles, limit],
  );
  const showSeeMoreStories = useMemo(
    () => articlesToRender.length < articles.length,
    [articles.length, articlesToRender.length],
  );

  return !articles ? (
    <ErrorBlock statusCode={404} />
  ) : (
    <>
      <NextSeo
        title="Blog"
        description="Access insights into some of the best automotive trends in Australia, information about Simplr car subscription and more."
      />
      <SectionWithContainer withPadding={false} className="py-6">
        <h1 className="mb-4 text-3xl font-light text-center text-dark-blue-100 md:text-4xl">
          Pure &amp; Simplr.
        </h1>
        <p className="mb-6 text-lg text-center">
          Keeping you in touch with the world of car subscription
        </p>
        <div className="grid grid-cols-1 -m-4 md:grid-cols-2 lg:grid-cols-3">
          {articlesToRender?.map(article => (
            <Link
              prefetch={false}
              href={`/blog/${article.slug}`}
              key={article.id}
            >
              <a className="flex w-full">
                <ArticleThumbnail className="flex-grow m-4" article={article} />
              </a>
            </Link>
          ))}
        </div>
        <div className="flex justify-center">
          {showSeeMoreStories && (
            <Button
              onClick={() => setLimit(limit + OFFSET_ARTICLES)}
              color="link"
              className="px-0 mt-4 font-bold text-coral-100"
            >
              <span>See more stories</span>
            </Button>
          )}
        </div>
      </SectionWithContainer>
      <MostPopularCarsSection />
    </>
  );
};

export default BlogsPage;
